import { useMemo, useState } from 'react'
import DashboardNavigation from './DashboardNavigation'
import FaqAnswerContent from './FaqAnswerContent'
import Footer from './Footer'
import {
  faqAnswerBlocksToPlainText,
  portalFaqIntro,
  portalFaqSections,
  type PortalFaqItem,
} from '../content/portalFaqData'
import {
  CLUB4X4_BUSINESS_HOURS,
  CLUB4X4_PHONE_DISPLAY,
  CLUB4X4_PHONE_TEL,
  CLUB4X4_WEBSITE_HREF,
} from '../claims/contactLinks'

/** Search icon for the portal FAQ filter input. */
function SearchIcon() {
  return (
    <svg className="w-5 h-5 text-text-placeholder" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  )
}

/** Plus icon shown when an FAQ accordion item is collapsed. */
function PlusIcon() {
  return (
    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

/** Minus icon shown when an FAQ accordion item is expanded. */
function MinusIcon() {
  return (
    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
    </svg>
  )
}

const FAQ_CARD_CLASS =
  'bg-white rounded-2xl w-full shadow-[0_1px_3px_rgba(26,43,60,0.08)]'

const FAQ_CARD_BUTTON_CLASS =
  'w-full flex items-center justify-between gap-[16px] px-[24px] py-[24px] md:px-[32px] md:py-[28px] text-left cursor-pointer'

const FAQ_CARD_ANSWER_CLASS = 'px-[24px] pb-[24px] md:px-[32px] md:pb-[28px] pt-0'

/** Stable, HTML-safe key for accordion state and ARIA ids (section titles may include spaces). */
function faqAccordionItemKey(sectionKey: string, index: number): string {
  const sectionSlug =
    sectionKey
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-') || 'section'
  return `${sectionSlug}-${index}`
}

interface FAQAccordionItemProps {
  item: PortalFaqItem
  itemKey: string
  isOpen: boolean
  onToggle: () => void
}

/** Single expandable FAQ question and answer card. */
function FAQAccordionItem({ item, itemKey, isOpen, onToggle }: FAQAccordionItemProps) {
  const buttonId = `faq-${itemKey}`
  const panelId = `${buttonId}-panel`

  return (
    <div className={FAQ_CARD_CLASS}>
      <button
        id={buttonId}
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={FAQ_CARD_BUTTON_CLASS}
      >
        <span className="font-body font-medium text-md leading-md text-[#1a2b3c] pr-[8px]">
          {item.question}
        </span>
        <span className="text-[#1a2b3c] shrink-0" aria-hidden>
          {isOpen ? <MinusIcon /> : <PlusIcon />}
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        aria-hidden={!isOpen}
        {...(!isOpen ? { inert: '' } : {})}
        className={`grid w-full transition-[grid-template-rows] duration-[250ms] ease-in-out motion-reduce:transition-none ${
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className={FAQ_CARD_ANSWER_CLASS}>
            <FaqAnswerContent blocks={item.answer} />
          </div>
        </div>
      </div>
    </div>
  )
}

interface FAQSectionComponentProps {
  title: string
  items: PortalFaqItem[]
  openItems: Record<string, boolean>
  onToggleItem: (key: string) => void
  sectionKey: string
}

/** Group of FAQ accordion items under one section heading. */
function FAQSectionComponent({ title, items, openItems, onToggleItem, sectionKey }: FAQSectionComponentProps) {
  return (
    <div className="flex flex-col gap-[16px] items-start w-full">
      <h2 className="font-display font-semibold text-lg leading-lg text-text-primary-900">{title}</h2>
      <div className="flex flex-col gap-[16px] items-stretch w-full">
        {items.map((item, index) => {
          const itemKey = faqAccordionItemKey(sectionKey, index)
          return (
            <FAQAccordionItem
              key={itemKey}
              item={item}
              itemKey={itemKey}
              isOpen={openItems[itemKey] ?? false}
              onToggle={() => onToggleItem(itemKey)}
            />
          )
        })}
      </div>
    </div>
  )
}

/** Return whether an FAQ item matches a case-insensitive search query. */
function faqItemMatchesQuery(item: PortalFaqItem, query: string): boolean {
  const normalized = query.toLowerCase()
  const answerText = faqAnswerBlocksToPlainText(item.answer).toLowerCase()
  return item.question.toLowerCase().includes(normalized) || answerText.includes(normalized)
}

/** Portal-specific FAQ page with search and accordion sections. */
export default function PortalFAQ() {
  const [searchQuery, setSearchQuery] = useState('')
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})

  const handleToggleItem = (key: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return portalFaqSections

    return portalFaqSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => faqItemMatchesQuery(item, searchQuery)),
      }))
      .filter((section) => section.items.length > 0)
  }, [searchQuery])

  return (
    <div className="flex flex-col items-start relative min-h-screen w-full bg-[#f1ece4]">
      <DashboardNavigation />

      <main id="main" className="flex w-full flex-col items-start">
      <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[20px] md:py-[32px] relative shrink-0 w-full">
        <div className="flex flex-col gap-0 items-center max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
          <div className="flex flex-col gap-[16px] md:gap-[24px] items-start md:items-center relative shrink-0 w-full">
            <div className="flex flex-col font-semibold gap-[12px] items-start md:items-center relative shrink-0 w-full">
              <p className="font-display font-semibold leading-sm relative shrink-0 text-foreground-brand-primary-alt text-sm md:text-md w-full md:text-center">
                Portal FAQ
              </p>
              <h1 className="font-display leading-[38px] md:leading-[44px] relative shrink-0 text-text-primary-on-brand text-display-sm md:text-display-md tracking-[-0.72px] w-full md:text-center">
                Club 4X4 Customer Portal FAQs
              </h1>
            </div>
            <p className="font-body font-regular text-md leading-md text-text-quaternary-on-brand w-full md:text-center">
              Everything you need to know about using your online Club 4X4 Customer Portal.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-[32px] md:gap-[48px] items-center px-[16px] md:px-[32px] py-[40px] md:py-[64px] relative w-full max-w-container-desktop mx-auto">
        <div className="w-full max-w-[600px] md:max-w-[720px]">
          <FaqAnswerContent blocks={portalFaqIntro} />
        </div>

        <div className="w-full max-w-[600px] md:max-w-[720px]">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-[14px] flex items-center pointer-events-none">
              <SearchIcon />
            </div>
            <input
              type="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search FAQs"
              className="bg-background-primary border border-border-primary rounded-lg w-full pl-[44px] pr-[14px] py-[12px] text-md text-text-primary-900 placeholder:text-text-placeholder focus:outline-none focus:ring-2 focus:ring-foreground-brand-primary-600 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex flex-col gap-[40px] md:gap-[48px] items-start w-full max-w-[600px] md:max-w-[720px]">
          {filteredSections.length > 0 ? (
            filteredSections.map((section) => (
              <FAQSectionComponent
                key={section.title}
                title={section.title}
                items={section.items}
                openItems={openItems}
                onToggleItem={handleToggleItem}
                sectionKey={section.title}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center w-full py-[40px]">
              <p className="font-body font-semibold text-md leading-md text-text-primary-900 mb-2">No results found</p>
              <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 text-center">
                Try searching for something else or browse our FAQ categories.
              </p>
            </div>
          )}
        </div>

        <p className="font-body font-regular text-md leading-md text-text-tertiary-600 text-center w-full max-w-[600px] md:max-w-[720px]">
          Still have a question? Call our team on{' '}
          <a href={CLUB4X4_PHONE_TEL} className="text-foreground-brand-primary-600 underline hover:text-foreground-brand-primary-700">
            {CLUB4X4_PHONE_DISPLAY}
          </a>{' '}
          ({CLUB4X4_BUSINESS_HOURS}) or visit{' '}
          <a
            href={CLUB4X4_WEBSITE_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground-brand-primary-600 underline hover:text-foreground-brand-primary-700"
          >
            club4x4.com.au
          </a>{' '}
          for more information.
        </p>
      </div>
      </main>

      <Footer />
    </div>
  )
}
